import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

/* ============================================================================
   ARTIFACT PANEL COMPONENT
   
   Slide-over panel for detailed artifact information.
   - Opens from right side
   - Full metadata display
   - Related items
   - Focus trapping
   - Background scroll lock
   - Deep-linkable via URL hash
   ============================================================================ */

// Metadata Row Component (defined outside to avoid recreation on every render)
const MetadataRow = ({ label, value, highlight = false }) => {
  if (!value || value === 'Not established') {
    return (
      <div style={{ marginBottom: '16px' }}>
        <dt style={{ 
          fontFamily: "'Source Sans 3', sans-serif", 
          fontSize: '12px', 
          fontWeight: 600, 
          letterSpacing: '0.08em', 
          textTransform: 'uppercase', 
          color: '#888',
          marginBottom: '4px'
        }}>
          {label}
        </dt>
        <dd style={{ 
          margin: 0, 
          fontFamily: "'Source Sans 3', sans-serif", 
          fontSize: '15px', 
          color: '#999',
          fontStyle: 'italic'
        }}>
          {value || 'Not established'}
        </dd>
      </div>
    );
  }
  
  return (
    <div style={{ marginBottom: '16px' }}>
      <dt style={{ 
        fontFamily: "'Source Sans 3', sans-serif", 
        fontSize: '12px', 
        fontWeight: 600, 
        letterSpacing: '0.08em', 
        textTransform: 'uppercase', 
        color: '#888',
        marginBottom: '4px'
      }}>
        {label}
      </dt>
      <dd style={{ 
        margin: 0, 
        fontFamily: "'Source Sans 3', sans-serif", 
        fontSize: '15px', 
        color: highlight ? '#8B7355' : '#1a1a1a',
        fontWeight: highlight ? 600 : 400
      }}>
        {value}
      </dd>
    </div>
  );
};

const ArtifactPanel = ({ artifact, isOpen, onClose }) => {
  const panelRef = useRef(null);
  const previousActiveElement = useRef(null);

  // Lock body scroll and trap focus
  useEffect(() => {
    if (isOpen) {
      previousActiveElement.current = document.activeElement;
      document.body.style.overflow = 'hidden';
      panelRef.current?.focus();
      
      // Update URL hash for deep linking
      if (artifact?.id) {
        window.history.pushState(null, '', `#artifact-${artifact.id}`);
      }
    } else {
      document.body.style.overflow = '';
      previousActiveElement.current?.focus();
      
      // Remove hash when closed
      if (window.location.hash.startsWith('#artifact-')) {
        window.history.pushState(null, '', window.location.pathname);
      }
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, artifact]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !artifact) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0, 0, 0, 0.5)',
          zIndex: 9998
        }}
        aria-hidden="true"
      />
      
      {/* Panel */}
      <aside
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={`Artifact details: ${artifact.title}`}
        tabIndex={-1}
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '100%',
          maxWidth: '520px',
          background: 'white',
          zIndex: 9999,
          overflowY: 'auto',
          boxShadow: '-4px 0 20px rgba(0,0,0,0.15)'
        }}
        data-testid="artifact-panel"
      >
        {/* Header */}
        <div style={{
          position: 'sticky',
          top: 0,
          background: 'white',
          borderBottom: '1px solid #e8e8e8',
          padding: '20px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 1
        }}>
          <div>
            <p style={{
              margin: '0 0 4px',
              fontFamily: "'Source Sans 3', sans-serif",
              fontSize: '11px',
              fontWeight: 700,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: '#8B7355'
            }}>
              {artifact.category}
            </p>
            <h2 style={{
              margin: 0,
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: '22px',
              fontWeight: 500,
              color: '#1a1a1a'
            }}>
              {artifact.title}
            </h2>
          </div>
          <button
            onClick={onClose}
            aria-label="Close panel"
            style={{
              width: '44px',
              height: '44px',
              background: '#f5f5f5',
              border: 'none',
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#666" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Image */}
        {artifact.image && (
          <div style={{ padding: '24px 24px 0' }}>
            <img 
              src={artifact.image} 
              alt={artifact.title}
              style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '4px' }}
            />
          </div>
        )}

        {/* Content */}
        <div style={{ padding: '24px' }}>
          {/* Catalog Reference */}
          {artifact.catalogRef && (
            <div style={{
              display: 'inline-block',
              padding: '6px 12px',
              background: '#f5f5f5',
              borderRadius: '4px',
              marginBottom: '20px'
            }}>
              <span style={{
                fontFamily: "'Source Sans 3', sans-serif",
                fontSize: '12px',
                fontWeight: 600,
                letterSpacing: '0.05em',
                color: '#666'
              }}>
                {artifact.catalogRef}
              </span>
            </div>
          )}

          {/* Description */}
          {artifact.description && (
            <p style={{
              margin: '0 0 24px',
              fontFamily: "'Source Serif 4', serif",
              fontSize: '16px',
              lineHeight: 1.75,
              color: '#444'
            }}>
              {artifact.description}
            </p>
          )}

          {/* Metadata */}
          <dl style={{ margin: 0 }}>
            <MetadataRow label="Date" value={artifact.date} />
            <MetadataRow label="Creator / Producer" value={artifact.creator} />
            <MetadataRow label="Physical Format" value={artifact.format} />
            <MetadataRow label="Dimensions" value={artifact.dimensions} />
            
            {artifact.psaCert && (
              <>
                <MetadataRow label="PSA Certification" value={artifact.psaCert} highlight />
                <MetadataRow label="PSA Grade" value={artifact.psaGrade} highlight />
                <MetadataRow label="PSA Designation" value={artifact.psaDesignation} />
              </>
            )}
            
            <MetadataRow label="Classification" value={artifact.classification} />
            <MetadataRow label="Admission Status" value={artifact.admissionStatus} />
            <MetadataRow label="Condition" value={artifact.condition} />
            <MetadataRow label="Provenance" value={artifact.provenance} />
            <MetadataRow label="Evidence Status" value={artifact.evidenceStatus} />
          </dl>

          {/* Historical Significance */}
          {artifact.significance && (
            <div style={{
              marginTop: '24px',
              padding: '20px',
              background: '#fafaf8',
              borderLeft: '3px solid #8B7355'
            }}>
              <h4 style={{
                margin: '0 0 8px',
                fontFamily: "'Source Sans 3', sans-serif",
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#8B7355'
              }}>
                Historical Significance
              </h4>
              <p style={{
                margin: 0,
                fontFamily: "'Source Sans 3', sans-serif",
                fontSize: '14px',
                lineHeight: 1.65,
                color: '#555'
              }}>
                {artifact.significance}
              </p>
            </div>
          )}

          {/* Related Artifacts */}
          {artifact.relatedItems && artifact.relatedItems.length > 0 && (
            <div style={{ marginTop: '24px' }}>
              <h4 style={{
                margin: '0 0 12px',
                fontFamily: "'Source Sans 3', sans-serif",
                fontSize: '13px',
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: '#888'
              }}>
                Related Artifacts
              </h4>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {artifact.relatedItems.map((item, idx) => (
                  <span 
                    key={idx}
                    style={{
                      padding: '6px 12px',
                      background: '#f0f0f0',
                      borderRadius: '4px',
                      fontFamily: "'Source Sans 3', sans-serif",
                      fontSize: '13px',
                      color: '#555'
                    }}
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      </aside>
    </>,
    document.body
  );
};

export default ArtifactPanel;
